import React, { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useCommonApi } from "src/_common/hooks/actions/common/appCommonApiHook";
import { validatePhoneNumber, getIso2FromDialCode } from "src/_common/utils/phoneValidationUtils";

interface ContactUsFormValues {
  name: string;
  email: string;
  subject: string;
  issue_type: string;
  message: string;
  phone: string;
  country_code: string;
}

const createContactUsSchema = (t: any, countryRef?: React.MutableRefObject<{ iso2: string; dialCode: string }>) =>
  yup.object().shape({
    name: yup.string().required(t("form.validation.nameRequired")).min(2, t("form.validation.nameMinLength")),
    email: yup.string().required(t("form.validation.emailRequired")).email(t("form.validation.emailInvalid")),
    subject: yup.string().required(t("form.validation.subjectRequired")).min(3, t("form.validation.subjectMinLength")),
    issue_type: yup.string().required(t("form.validation.issueTypeRequired")),
    message: yup.string().required(t("form.validation.messageRequired")).min(10, t("form.validation.messageMinLength")),
    phone: yup
      .string()
      .required(t("form.validation.phoneRequired"))
      .test("phone-length", "", function (value) {
        if (!value || !countryRef) return true;
        const { iso2, dialCode } = countryRef.current;
        const result = validatePhoneNumber(value, dialCode, iso2);
        if (!result.valid) {
          if (result.expectedMin === result.expectedMax) {
            return this.createError({
              message: `Mobile number must be ${result.expectedMin} digits (currently ${result.actualLength})`,
            });
          }
          return this.createError({
            message: `Mobile number must be between ${result.expectedMin} and ${result.expectedMax} digits (currently ${result.actualLength})`,
          });
        }
        return true;
      }),
    country_code: yup.string(),
  });

export default function ContactUsPage() {
  const { t } = useTranslation("contact-us");
  const toast = useToaster();
  const { callContactSupport } = useCommonApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const countryRef = useRef<{ iso2: string; dialCode: string }>({ iso2: "us", dialCode: "1" });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ContactUsFormValues>({
    resolver: yupResolver(createContactUsSchema(t, countryRef)),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      issue_type: "",
      message: "",
      phone: "",
      country_code: "+1",
    },
  });

  const onSubmit = (values: ContactUsFormValues) => {
    setIsSubmitting(true);

    // Extract phone number and country code from PhoneInput
    const phoneNumber = (values.phone || "").replace(/\D/g, ""); // Remove all non-digits
    const countryCode = (values.country_code || "+1").replace("+", ""); // Get country code from state

    // Remove country code from phone number to get mobile
    let mobile = phoneNumber;
    if (phoneNumber.startsWith(countryCode)) {
      mobile = phoneNumber.substring(countryCode.length);
    }

    const params = {
      name: values.name,
      email: values.email,
      subject: values.subject,
      report_type: values.issue_type,
      message: values.message,
      phone_code: values.country_code || "+1", // Use the country_code from state (already has +)
      phone: mobile,
    };

    console.log("Contact support params:", params);

    callContactSupport(
      params,
      (message: string, data: any) => {
        console.log("Contact support success:", data);
        toast.success(t("messages.success"));
        reset();
        setIsSubmitting(false);
      },
      (error: any) => {
        console.error("Contact support error:", error);
        toast.error(error?.message || t("messages.error"));
        setIsSubmitting(false);
      },
    );
  };

  return (
    <div className="dashboard-main member-page-wrapper contact-us-wrapper">
      <div id="content">
        {/* Header Section */}
        <div className="profile-header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="page-title">{t("pageTitle")}</h1>
            </div>
          </div>
        </div>

        <div className="profile-content">
          <div className="container-fluid px-4">
            <div className="form-section">
              <div className="section-header">
                <h2 className="section-title">{t("header.title")}</h2>
                <p className="section-subtitle">{t("header.subtitle")}</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="edit-profile-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      {t("form.fields.name.label")} <span className="mandatory-mark">*</span>
                    </label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ onChange, onBlur, value }) => (
                        <input
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value || ""}
                          type="text"
                          className={`form-control ${errors.name ? "is-invalid" : ""}`}
                          placeholder={t("form.fields.name.placeholder")}
                        />
                      )}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t("form.fields.email.label")} <span className="mandatory-mark">*</span>
                    </label>
                    <Controller
                      name="email"
                      control={control}
                      render={({ onChange, onBlur, value }) => (
                        <input
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value || ""}
                          type="email"
                          className={`form-control ${errors.email ? "is-invalid" : ""}`}
                          placeholder={t("form.fields.email.placeholder")}
                        />
                      )}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t("form.fields.subject.label")} <span className="mandatory-mark">*</span>
                    </label>
                    <Controller
                      name="subject"
                      control={control}
                      render={({ onChange, onBlur, value }) => (
                        <input
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value || ""}
                          type="text"
                          className={`form-control ${errors.subject ? "is-invalid" : ""}`}
                          placeholder={t("form.fields.subject.placeholder")}
                        />
                      )}
                    />
                    {errors.subject && <div className="invalid-feedback">{errors.subject.message}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t("form.fields.issueType.label")} <span className="mandatory-mark">*</span>
                    </label>
                    <Controller
                      name="issue_type"
                      control={control}
                      render={({ onChange, onBlur, value }) => (
                        <select
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value || ""}
                          className={`basic-select ${errors.issue_type ? "is-invalid" : ""}`}
                        >
                          <option value="">{t("form.fields.issueType.placeholder")}</option>
                          <option value="Billing issue">{t("form.fields.issueType.options.billingIssue")}</option>
                          <option value="Account issue">{t("form.fields.issueType.options.accountIssue")}</option>
                          <option value="Subscription renewal issue">
                            {t("form.fields.issueType.options.subscriptionRenewal")}
                          </option>
                          <option value="Plan change">{t("form.fields.issueType.options.planChange")}</option>
                          <option value="Payment related">{t("form.fields.issueType.options.paymentRelated")}</option>
                          <option value="Invoice">{t("form.fields.issueType.options.invoice")}</option>
                          <option value="Others">{t("form.fields.issueType.options.others")}</option>
                        </select>
                      )}
                    />
                    {errors.issue_type && <div className="invalid-feedback">{errors.issue_type.message}</div>}
                  </div>

                  <div className="figma-form-group">
                    <label className="figma-label">
                      {t("form.fields.phone.label")} <span className="mandatory-mark">*</span>
                    </label>
                    <div className="figma-phone-wrapper" style={{ zIndex: 99 }}>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <PhoneInput
                            country={"us"}
                            value={value || ""}
                            onChange={(phone: string, country: any) => {
                              onChange(phone);
                              setValue("country_code", `+${country.dialCode}`);
                              countryRef.current = { iso2: country.countryCode, dialCode: country.dialCode };
                            }}
                            onBlur={onBlur}
                            placeholder={t("form.fields.phone.placeholder")}
                            countryCodeEditable={false}
                            inputClass={`figma-phone-input ${errors.phone ? "error" : ""}`}
                          />
                        )}
                      />
                    </div>
                    {errors.phone && <span className="figma-error-text">{errors.phone.message}</span>}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label className="form-label">
                    {t("form.fields.message.label")} <span className="mandatory-mark">*</span>
                  </label>
                  <Controller
                    name="message"
                    control={control}
                    render={({ onChange, onBlur, value }) => (
                      <textarea
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value || ""}
                        className={`form-control ${errors.message ? "is-invalid" : ""}`}
                        placeholder={t("form.fields.message.placeholder")}
                        rows={6}
                        style={{ resize: "vertical", minHeight: "150px" }}
                      />
                    )}
                  />
                  {errors.message && <div className="invalid-feedback">{errors.message.message}</div>}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-preview" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <i className="fa fa-spinner fa-spin me-2"></i>
                        {t("form.buttons.sending")}
                      </>
                    ) : (
                      <>
                        <i className="fa fa-paper-plane me-2"></i>
                        {t("form.buttons.send")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
